import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import CampaignLeadsTable from "@/components/admin/CampaignLeadsTable";
import ConversionReport from "@/components/admin/ConversionReport";

export const Route = createFileRoute("/_admin/admin_/leads")({
  head: () => ({ meta: [{ title: "Leads | Admin" }, { name: "robots", content: "noindex" }] }),
  component: LeadsDashboard,
});

const TZ = "America/New_York";
type Row = {
  id: string; created_at: string; event: string; page_path: string | null; link_location: string | null;
  utm_source: string | null; utm_medium: string | null; utm_content: string | null; utm_campaign: string | null;
  session_id: string | null; device: string | null; referrer: string | null;
  landing_page: string | null; detail: Record<string, string> | null;
};
type RangeKey = "today" | "7d" | "30d" | "month" | "custom";

const LEAD_ACTIONS = new Set(["phone_click", "sms_click", "sms_copy", "phone_copy", "email_click", "check_date_click", "quote_form_start", "generate_lead", "text_us_click", "quote_form_submit", "corporate_lead"]);

function etDate(d: Date) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
/** UTC instant of midnight Atlanta time on a YYYY-MM-DD date. */
function etMidnight(day: string) {
  const guess = new Date(`${day}T00:00:00Z`);
  const asEt = new Date(guess.toLocaleString("en-US", { timeZone: TZ }));
  const asUtc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(guess.getTime() + (asUtc.getTime() - asEt.getTime()));
}
function addDays(day: string, n: number) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { timeZone: TZ, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function countBy(rows: Row[], key: (r: Row) => string) {
  const m: Record<string, number> = {};
  for (const r of rows) { const k = key(r); m[k] = (m[k] ?? 0) + 1; }
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
}

function LeadsDashboard() {
  const today = etDate(new Date());
  const [range, setRange] = useState<RangeKey>("7d");
  const [customFrom, setCustomFrom] = useState(addDays(today, -6));
  const [customTo, setCustomTo] = useState(today);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [from, to] = useMemo((): [string, string] => {
    switch (range) {
      case "today": return [today, today];
      case "7d": return [addDays(today, -6), today];
      case "30d": return [addDays(today, -29), today];
      case "month": return [today.slice(0, 8) + "01", today];
      default: return [customFrom, customTo];
    }
  }, [range, today, customFrom, customTo]);

  const load = async () => {
    setLoading(true); setErr(null);
    const all: Row[] = [];
    for (let page = 0; page < 20; page++) {
      const { data, error } = await supabase
        .from("lead_events")
        .select("id,created_at,event,page_path,link_location,utm_source,utm_medium,utm_content,utm_campaign,session_id,device,referrer,landing_page,detail")
        .gte("created_at", etMidnight(from).toISOString())
        .lt("created_at", etMidnight(addDays(to, 1)).toISOString())
        .order("created_at", { ascending: false })
        .range(page * 1000, page * 1000 + 999);
      if (error) { setErr(error.message); break; }
      all.push(...((data ?? []) as Row[]));
      if (!data || data.length < 1000) break;
    }
    setRows(all); setLoading(false);
  };
  useEffect(() => { load(); }, [from, to]);

  const n = (...evs: string[]) => rows.filter((r) => evs.includes(r.event)).length;
  const leads = rows.filter((r) => LEAD_ACTIONS.has(r.event));
  const cards: [string, number][] = [
    ["Calls", n("phone_click")],
    ["Texts", n("sms_click", "sms_copy", "text_us_click")],
    ["Emails", n("email_click")],
    ["Form views", n("quote_form_view")],
    ["Form starts", n("quote_form_start")],
    ["Form submissions", n("generate_lead", "quote_form_submit", "corporate_lead")],
    ["Total lead actions", leads.length],
    ["Sessions with a lead action", new Set(leads.map((r) => r.session_id)).size],
  ];

  const daily = useMemo(() => {
    const m: Record<string, number> = {};
    for (let d = from; d <= to; d = addDays(d, 1)) m[d] = 0;
    for (const r of leads) { const d = etDate(new Date(r.created_at)); if (d in m) m[d]++; }
    return Object.entries(m).map(([day, count]) => ({ day: day.slice(5), count }));
  }, [leads, from, to]);

  const tables: [string, [string, number][]][] = [
    ["By source", countBy(leads, (r) => (r.utm_source ? `${r.utm_source} / ${r.utm_medium || "(none)"}` : "Direct / unknown"))],
    ["By utm_content", countBy(leads, (r) => r.utm_content || "(none)")],
    ["By page", countBy(leads, (r) => r.page_path || "(unknown)")],
    ["By button location", countBy(leads, (r) => r.link_location || "page")],
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <Link to="/admin" className="text-xs text-muted-foreground tracking-[0.2em] uppercase">← Quote Requests</Link>
            <h1 className="font-heading text-xl font-semibold">Leads</h1>
          </div>
          <button onClick={load} className="text-sm flex items-center gap-2 text-muted-foreground hover:text-foreground">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        <p className="text-sm rounded-md border border-border bg-accent/40 p-4 text-foreground">
          Form submissions are counted only when the Limo Anywhere widget reports them. Check Limo Anywhere for the full list of quote requests.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          {([["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["month", "This month"], ["custom", "Custom"]] as const).map(([k, label]) => (
            <button key={k} onClick={() => setRange(k)}
              className={`px-4 py-2 rounded-md text-sm border ${range === k ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}>
              {label}
            </button>
          ))}
          {range === "custom" && (
            <span className="flex items-center gap-2 text-sm">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border border-border rounded-md px-2 py-1.5 bg-card" />
              to
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border border-border rounded-md px-2 py-1.5 bg-card" />
            </span>
          )}
          <span className="text-xs text-muted-foreground ml-auto">Atlanta time · {from} to {to}</span>
        </div>

        {err && <p className="text-destructive text-sm">{err}</p>}
        <CampaignLeadsTable fromIso={etMidnight(from).toISOString()} toIso={etMidnight(addDays(to, 1)).toISOString()} />
        {loading ? <p className="text-muted-foreground">Loading…</p> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {cards.map(([label, v]) => (
                <div key={label} className="luxury-card rounded-lg p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
                  <p className="font-heading text-3xl font-semibold mt-1">{v}</p>
                </div>
              ))}
            </div>

            <ConversionReport rows={rows} />

            <div className="luxury-card rounded-lg p-4">
              <h2 className="font-semibold mb-3">Lead actions per day</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="count" name="Lead actions" fill="var(--color-primary, currentColor)" className="fill-primary" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {tables.map(([title, data]) => (
                <div key={title} className="luxury-card rounded-lg p-4">
                  <h2 className="font-semibold mb-3">{title}</h2>
                  {data.length === 0 ? <p className="text-sm text-muted-foreground">No lead actions yet.</p> : (
                    <table className="w-full text-sm">
                      <tbody>
                        {data.map(([k, v]) => (
                          <tr key={k} className="border-t border-border">
                            <td className="py-1.5 pr-2 break-all">{k}</td>
                            <td className="py-1.5 text-right font-semibold">{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>

            <div className="luxury-card rounded-lg p-4 overflow-x-auto">
              <h2 className="font-semibold mb-3">Most recent 50 events</h2>
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr><th className="py-1.5 pr-3">Time</th><th className="pr-3">Event</th><th className="pr-3">Page</th><th className="pr-3">Location</th><th className="pr-3">Source</th><th className="pr-3">utm_content</th><th>Device</th></tr>
                </thead>
                <tbody>
                  {rows.filter((r) => r.event !== "landing_view" && r.event !== "page_view").slice(0, 50).map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="py-1.5 pr-3 whitespace-nowrap">{fmtTime(r.created_at)}</td>
                      <td className="pr-3">{r.event}</td>
                      <td className="pr-3">{r.page_path}</td>
                      <td className="pr-3">{r.link_location}</td>
                      <td className="pr-3">{r.utm_source ? `${r.utm_source} / ${r.utm_medium || "(none)"}` : "Direct / unknown"}</td>
                      <td className="pr-3">{r.utm_content}</td>
                      <td>{r.device}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
